<template>
    <div class="bg-white p-6">
        <!--
            Two columns from xl up: questions left, the RocketLawyer draft
            preview right (RAC-10332). The preview sits OUTSIDE the v-if chain
            below on purpose, so it survives the finalizing state — "Generating
            your lease…" then appears beside the document being generated.

            min-w-0 on both children is load-bearing, not decoration: grid items
            default to min-width:auto, and a 55KB legal document would otherwise
            push the column wider than its track and give the whole card
            horizontal scroll.
        -->
        <div class="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div class="min-w-0">
                <!-- Loading state -->
                <div v-if="loading && !page" class="space-y-4">
                    <div class="h-2 bg-gray-200 rounded animate-pulse"></div>
                    <div class="h-6 w-1/2 bg-gray-200 rounded animate-pulse"></div>
                    <div class="space-y-3">
                        <div v-for="n in 4" :key="n" class="h-10 bg-gray-100 rounded animate-pulse"></div>
                    </div>
                </div>

                <!-- Fatal error (no questions on first load) -->
                <div
                    v-else-if="errorMessage && !page"
                    class="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-4"
                >
                    {{ errorMessage }}
                </div>

                <div v-else-if="finalizing" class="py-12 text-center space-y-3">
                    <div class="w-full max-w-xs mx-auto bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div class="bg-purple-600 h-2 rounded-full animate-pulse w-full"></div>
                    </div>
                    <p class="text-gray-700 font-medium">Generating your {{ documentLabel }}…</p>
                    <p class="text-sm text-gray-500">This may take a few moments.</p>
                </div>

                <template v-else-if="page">
                    <!-- Eviction notices only (RAC-10343). Every other document
                         here is negotiated between the parties; this one starts a
                         legal clock, and a notice with the wrong reason or period
                         gets the case dismissed. Say plainly that we aren't the
                         lawyer, and point at the one the sidebar already offers. -->
                    <div
                        v-if="isEvictionFlow"
                        class="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4"
                    >
                        <p class="text-sm text-amber-900">
                            This form builds a state-specific notice, but it isn't legal advice —
                            eviction rules and notice periods vary by state and by situation.
                        </p>
                        <p class="text-sm text-amber-900 mt-1">
                            <strong>Book a Call</strong> with a landlord attorney to review it before
                            you serve it.
                        </p>
                    </div>

                    <!-- Progress indicator: RL provides progressPercentage per page;
                         we surface that but NOT a "Page X" number — RL doesn't expose
                         a total page count (pages branch by answer), so any denominator
                         we made up would be misleading. -->
                    <div class="mb-6">
                        <div class="flex items-center justify-end text-xs text-gray-500 mb-2">
                            <span>{{ page.progress.percent }}% complete</span>
                        </div>
                        <div class="w-full bg-gray-200 rounded-full h-2">
                            <div
                                class="bg-purple-600 h-2 rounded-full transition-all duration-300"
                                :style="{ width: page.progress.percent + '%' }"
                            ></div>
                        </div>
                    </div>

                    <!-- Editing an already-generated document/lease resumes at the page it
                         was finished on — typically 80-95% of the way through — so say so
                         rather than leaving the landlord wondering where the earlier
                         questions went. Clears as soon as they navigate. -->
                    <div
                        v-if="showResumeHint"
                        class="bg-rent-risk-blue-100 border border-rent-risk-blue-400/20 rounded-lg px-4 py-3 mb-4 flex items-start gap-2"
                    >
                        <svg class="w-4 h-4 text-rent-risk-blue-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <p class="text-xs text-gray-600">You're picking up where you left off — use <strong>Back</strong> to review earlier answers.</p>
                    </div>

                    <!-- Page title -->
                    <h3 v-if="page.title" class="text-lg font-semibold text-gray-800 mb-4">
                        {{ page.title }}
                    </h3>

                    <!-- Terminal state: every question answered, ready to finalize -->
                    <div
                        v-if="page.is_terminal"
                        class="bg-purple-50 border border-purple-200 rounded p-4 mb-4 text-sm text-gray-700"
                    >
                        <p class="font-semibold mb-1">You've answered every question.</p>
                        <p>Click <strong>{{ generateLabel }}</strong> below to finalize your {{ documentLabel }}.
                        Hit <strong>Back</strong> if you want to review the previous page first.</p>
                    </div>

                    <form @submit.prevent="handleNext" class="space-y-6">
                        <!-- Cyclical page: render N rows of the same fields with Add another / Remove -->
                        <template v-if="isCyclicalPage">
                            <fieldset
                                v-for="(row, rowIdx) in cycleRows"
                                :key="`row-${rowIdx}`"
                                class="border border-gray-200 rounded p-4 relative"
                            >
                                <legend class="text-sm font-medium text-gray-800 px-1">
                                    Entry {{ rowIdx + 1 }}
                                </legend>

                                <button
                                    v-if="cycleRows.length > 1"
                                    type="button"
                                    @click="removeCycleRow(rowIdx)"
                                    class="absolute top-2 right-2 text-xs text-red-600 hover:text-red-800"
                                >
                                    Remove
                                </button>

                                <div
                                    v-for="question in page.questions"
                                    :key="`q-${question.id}-row-${rowIdx}`"
                                    class="space-y-3"
                                >
                                    <p
                                        v-if="shouldRenderQuestionTitle(question) && rowIdx === 0"
                                        class="text-sm font-medium text-gray-800 mb-1"
                                    >
                                        {{ question.title }}
                                    </p>
                                    <p v-if="question.hint && rowIdx === 0" class="text-xs text-gray-500 mb-2">
                                        {{ question.hint }}
                                    </p>

                                    <div v-for="field in question.fields" :key="field.id">
                                        <label
                                            v-if="field.label"
                                            :for="`rl-cycle-${rowIdx}-${field.id}`"
                                            class="block text-sm text-gray-700 mb-1"
                                        >
                                            {{ field.label }}
                                        </label>
                                        <component
                                            :is="fieldComponent(field)"
                                            v-bind="fieldBindings(field, `rl-cycle-${rowIdx}-${field.id}`)"
                                            :value="row[field.id] ?? ''"
                                            @input="(e) => updateCycleField(rowIdx, field, e)"
                                            @change="(e) => updateCycleField(rowIdx, field, e)"
                                        >
                                            <template v-if="field.type === 'DROPDOWN'">
                                                <option value="" disabled>Choose…</option>
                                                <option
                                                    v-for="opt in (field.options || [])"
                                                    :key="opt"
                                                    :value="opt"
                                                    :selected="row[field.id] === opt"
                                                >{{ opt }}</option>
                                            </template>
                                        </component>
                                    </div>
                                </div>
                            </fieldset>

                            <button
                                type="button"
                                @click="addCycleRow"
                                class="closeButton self-start"
                            >
                                + Add another
                            </button>
                        </template>

                        <!-- Standard (non-cyclical) page: render flat questions -->
                        <template v-else>
                            <fieldset
                                v-for="question in visibleStandardQuestions"
                                :key="question.id"
                                class="border-t border-gray-100 pt-4 first:border-0 first:pt-0"
                            >
                                <legend
                                    v-if="shouldRenderQuestionTitle(question)"
                                    class="block text-sm font-medium text-gray-800 mb-1"
                                >
                                    {{ question.title }}
                                </legend>
                                <p v-if="question.hint" class="text-xs text-gray-500 mb-3">{{ question.hint }}</p>

                                <div
                                    v-if="isRadioQuestion(question)"
                                    class="space-y-2"
                                >
                                    <label
                                        v-for="option in question.fields"
                                        :key="option.id"
                                        class="flex items-center cursor-pointer"
                                    >
                                        <input
                                            type="radio"
                                            :name="`rl-q-${question.id}`"
                                            :value="option.id"
                                            :checked="formAnswers[option.id] === true"
                                            @change="selectRadio(question, option.id)"
                                            class="text-purple-600 focus:ring-purple-500 border-gray-300"
                                        >
                                        <span class="ml-2 text-sm text-gray-700">{{ option.label }}</span>
                                    </label>
                                </div>

                                <div v-else class="space-y-4">
                                    <div v-for="field in question.fields" :key="field.id">
                                        <!-- RADIO can appear beside other field types; grouped by question id -->
                                        <label
                                            v-if="field.type === 'RADIO'"
                                            class="flex items-center cursor-pointer"
                                        >
                                            <input
                                                :id="`rl-field-${field.id}`"
                                                type="radio"
                                                :name="`rl-q-${question.id}`"
                                                :value="field.id"
                                                :checked="formAnswers[field.id] === true"
                                                @change="selectRadio(question, field.id)"
                                                class="text-purple-600 focus:ring-purple-500 border-gray-300"
                                            >
                                            <span class="ml-2 text-sm text-gray-700">{{ field.label }}</span>
                                        </label>

                                        <!-- CHECKBOX renders as label+input row, no separate <label> above -->
                                        <label
                                            v-else-if="field.type === 'CHECKBOX'"
                                            class="flex items-center cursor-pointer"
                                        >
                                            <input
                                                :id="`rl-field-${field.id}`"
                                                type="checkbox"
                                                :checked="formAnswers[field.id] === true"
                                                @change="(e) => (formAnswers[field.id] = e.target.checked)"
                                                class="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                            >
                                            <span class="ml-2 text-sm text-gray-700">{{ field.label }}</span>
                                        </label>

                                        <template v-else>
                                        <label
                                            :for="`rl-field-${field.id}`"
                                            v-if="field.label"
                                            class="block text-sm text-gray-700 mb-1"
                                        >
                                            {{ field.label }}
                                        </label>

                                        <select
                                            v-if="field.type === 'DROPDOWN'"
                                            :id="`rl-field-${field.id}`"
                                            v-model="formAnswers[field.id]"
                                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                        >
                                            <option value="" disabled>Choose…</option>
                                            <option
                                                v-for="opt in (field.options || [])"
                                                :key="opt"
                                                :value="opt"
                                            >{{ opt }}</option>
                                        </select>

                                        <input
                                            v-else-if="field.type === 'DATE'"
                                            :id="`rl-field-${field.id}`"
                                            v-model="formAnswers[field.id]"
                                            type="date"
                                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                        >

                                        <input
                                            v-else-if="field.type === 'CURRENCY' || field.type === 'NUMBER'"
                                            :id="`rl-field-${field.id}`"
                                            v-model.number="formAnswers[field.id]"
                                            type="number"
                                            :step="field.type === 'CURRENCY' ? '0.01' : '1'"
                                            :min="field.type === 'CURRENCY' ? '0' : undefined"
                                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                        >

                                        <textarea
                                            v-else-if="field.type === 'PARAGRAPH'"
                                            :id="`rl-field-${field.id}`"
                                            v-model="formAnswers[field.id]"
                                            rows="4"
                                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                        ></textarea>

                                        <input
                                            v-else-if="field.type === 'PHONE_NUMBER'"
                                            :id="`rl-field-${field.id}`"
                                            v-model="formAnswers[field.id]"
                                            type="tel"
                                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                        >

                                        <input
                                            v-else
                                            :id="`rl-field-${field.id}`"
                                            v-model="formAnswers[field.id]"
                                            type="text"
                                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                        >
                                        </template>
                                    </div>
                                </div>

                                <details v-if="question.help" class="mt-3 text-xs text-gray-500">
                                    <summary class="cursor-pointer text-purple-700 hover:text-purple-900">More about this</summary>
                                    <div class="prose prose-sm mt-2" v-html="question.help"></div>
                                </details>
                            </fieldset>
                        </template>

                        <!-- Inline error -->
                        <p v-if="errorMessage" class="text-sm text-red-600 bg-red-50 p-2 rounded">
                            {{ errorMessage }}
                        </p>

                        <!-- Navigation -->
                        <div class="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t border-gray-100">
                            <button
                                type="button"
                                @click="handlePrevious"
                                :disabled="!page.nav.has_previous || submitting"
                                class="closeButton"
                                :class="{ 'opacity-25 cursor-not-allowed': !page.nav.has_previous || submitting }"
                            >
                                Back
                            </button>

                            <div class="flex flex-col sm:flex-row gap-3">
                                <button
                                    type="button"
                                    @click="handleSave"
                                    :disabled="submitting"
                                    class="closeButton"
                                    :class="{ 'opacity-25': submitting }"
                                >
                                    Save &amp; Continue Later
                                </button>

                                <button
                                    v-if="!page.nav.is_last_page"
                                    type="submit"
                                    :disabled="submitting"
                                    class="primaryButton"
                                    :class="{ 'opacity-25': submitting }"
                                >
                                    {{ submitting ? 'Saving…' : 'Next' }}
                                </button>

                                <button
                                    v-else
                                    type="button"
                                    @click="handleComplete"
                                    :disabled="submitting"
                                    class="primaryButton"
                                    :class="{ 'opacity-25': submitting }"
                                >
                                    {{ submitting ? 'Finalizing…' : generateLabel }}
                                </button>
                            </div>
                        </div>
                    </form>
                </template>
            </div>

            <RocketLawyerDraftPreview
                class="min-w-0"
                :html="previewHtml"
                :busy="submitting"
                :document-label="documentLabel"
                :withheld-message="withheldPreview"
            />
        </div>
    </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import axios from 'axios'
import { router } from '@inertiajs/vue3'
import { visibleQuestions } from './rocketLawyerShowIf'
import { resolvePreview } from './rocketLawyerPreview'
import { firstUnansweredRadioQuestion, shouldSuppressRadioDefaults } from './evictionRadioChoice'
import { withheldPreviewMessage } from './evictionPreviewGate'
import RocketLawyerDraftPreview from './RocketLawyerDraftPreview.vue'

const props = defineProps({
    propertyId: {
        type: [Number, String],
        required: true,
    },
    initialInterview: {
        type: Object,
        default: null,
    },
    restart: {
        type: Boolean,
        default: false,
    },
    tenants: {
        type: Array,
        default: () => [],
    },
    // Non-lease documents (RAC-10324): 'eviction_notice' or 'form' switch the
    // wizard onto the interview-keyed document-wizard routes; the default keeps
    // today's lease flow byte-for-byte.
    category: {
        type: String,
        default: 'lease_agreement',
    },
    // Exact catalog name, required when category is 'form'.
    templateName: {
        type: String,
        default: null,
    },
    // An existing in-progress document interview to resume (documents accumulate,
    // so resume is by id — unlike the lease singleton).
    interviewId: {
        type: [Number, String],
        default: null,
    },
    // Display name for copy ("Eviction Notice", "Roommate Agreement").
    documentName: {
        type: String,
        default: 'Lease',
    },
})

const emit = defineEmits(['completed', 'saved', 'upgrade-required'])

const interview = ref(props.initialInterview ?? null)
const page = ref(null)
// Set only when the wizard opens on an already-completed interview (the Edit
// path); dismissed on the first navigation so it never nags.
const showResumeHint = ref(false)
const formAnswers = reactive({})
const cycleRows = ref([])
const loading = ref(false)
const submitting = ref(false)
const finalizing = ref(false)
const errorMessage = ref(null)
// The draft preview is deliberately NOT part of `page`: RocketLawyer returns it
// on some page responses and not others, so it has its own lifetime. A response
// without one means "nothing new to show", never "the document is gone", and we
// never issue an extra RocketLawyer call to chase one (RAC-10332).
const previewHtml = ref(null)

const isLeaseFlow = computed(() => props.category === 'lease_agreement')
const isEvictionFlow = computed(() => props.category === 'eviction_notice')
const generateLabel = computed(() => (isLeaseFlow.value ? 'Generate Lease' : `Generate ${props.documentName}`))
const documentLabel = computed(() => (isLeaseFlow.value ? 'lease' : props.documentName.toLowerCase()))

// Lease routes are property-keyed (the singleton); document routes are
// interview-keyed and only known once the interview exists.
const baseUrl = computed(() => {
    if (isLeaseFlow.value) {
        return `/properties/${props.propertyId}/lease-wizard`
    }
    const id = interview.value?.id ?? props.interviewId
    return `/properties/${props.propertyId}/document-wizard/${id}`
})
const isCyclicalPage = computed(() => page.value?.type === 'cyclical' && !!page.value?.cycle_id)

// RocketLawyer gates follow-up questions behind per-question `showIf` rules
// (e.g. "(petsAllowed = true)"). Evaluate them reactively against the current
// answers so conditional questions appear/disappear as the user toggles the
// controlling radio — and so we never submit answers to a hidden question.
const visibleStandardQuestions = computed(() => {
    if (!page.value || isCyclicalPage.value) {
        return []
    }
    return visibleQuestions(page.value.questions || [], formAnswers)
})

const visibleStandardFieldIds = computed(() => {
    const ids = new Set()
    for (const question of visibleStandardQuestions.value) {
        for (const field of question.fields || []) {
            if (field && field.id != null) {
                ids.add(field.id)
            }
        }
    }
    return ids
})

const isRadioQuestion = (question) => {
    if (!question.fields || question.fields.length === 0) return false
    return question.fields.every((f) => f.type === 'RADIO')
}

// Why the draft preview is being held back, or null when it may be shown. An
// eviction preview is RocketLawyer's guess at the reason until the landlord
// answers that question themselves — see evictionPreviewGate.js.
const withheldPreview = computed(() => withheldPreviewMessage({
    isEviction: isEvictionFlow.value,
    reasonAnswered: interview.value?.eviction_reason_answered === true,
    isTerminalPage: page.value?.is_terminal === true,
}))

// A radio question on this page with nothing chosen. Scoped to eviction notices
// deliberately: the lease flow still adopts RocketLawyer's defaults, so gating
// Next there would change behaviour this ticket has no business touching.
const unansweredRadioQuestion = computed(() => {
    if (!isEvictionFlow.value) {
        return null
    }

    return firstUnansweredRadioQuestion(visibleStandardQuestions.value, formAnswers)
})

const shouldRenderQuestionTitle = (question) => {
    if (!question.title) return false
    if (!page.value?.title) return true
    return question.title.trim() !== page.value.title.trim()
}

const selectRadio = (question, selectedFieldId) => {
    // RADIO fields only. A radio can share a question with text inputs, and
    // setting one of those to false would wipe what the landlord typed.
    for (const option of question.fields) {
        if (option.type !== 'RADIO') continue
        formAnswers[option.id] = option.id === selectedFieldId
    }

    // Clear the "choose an answer" prompt the moment they do.
    if (errorMessage.value) {
        errorMessage.value = null
    }
}

const defaultValueForField = (field) => {
    if (field.type === 'RADIO') return field.default === true
    if (field.type === 'CURRENCY' || field.type === 'NUMBER') return field.default ?? ''
    return field.default ?? ''
}

const blankCycleRow = () => {
    const row = {}
    for (const question of page.value?.questions || []) {
        for (const field of question.fields || []) {
            row[field.id] = defaultValueForField(field)
        }
    }
    return row
}

const addCycleRow = () => {
    cycleRows.value.push(blankCycleRow())
}

const removeCycleRow = (idx) => {
    if (cycleRows.value.length <= 1) return
    cycleRows.value.splice(idx, 1)
}

const updateCycleField = (rowIdx, field, event) => {
    const target = event?.target
    let value
    if (target && target.type === 'checkbox') {
        value = target.checked
    } else if (target && target.type === 'number') {
        value = target.value === '' ? '' : Number(target.value)
    } else {
        value = target?.value ?? ''
    }
    cycleRows.value[rowIdx] = { ...cycleRows.value[rowIdx], [field.id]: value }
}

const fieldComponent = (field) => {
    if (field.type === 'DROPDOWN') return 'select'
    if (field.type === 'PARAGRAPH') return 'textarea'
    return 'input'
}

const fieldBindings = (field, id) => {
    const bindings = { id, class: 'block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm' }
    if (field.type === 'DROPDOWN') return bindings
    if (field.type === 'PARAGRAPH') return { ...bindings, rows: 4 }
    if (field.type === 'DATE') return { ...bindings, type: 'date' }
    if (field.type === 'CURRENCY') return { ...bindings, type: 'number', step: '0.01', min: '0' }
    if (field.type === 'NUMBER') return { ...bindings, type: 'number', step: '1' }
    if (field.type === 'PHONE_NUMBER') return { ...bindings, type: 'tel' }
    return { ...bindings, type: 'text' }
}

const start = async () => {
    loading.value = true
    errorMessage.value = null
    try {
        let data
        if (isLeaseFlow.value) {
            const payload = { restart: props.restart }
            // The roster only seeds a brand-new interview; on resume it's omitted
            // (the backend ignores it once a remote interview exists).
            if (Array.isArray(props.tenants) && props.tenants.length > 0) {
                payload.tenants = props.tenants
            }
            ;({ data } = await axios.post(baseUrl.value, payload))
        } else if (props.interviewId) {
            ;({ data } = await axios.post(`/properties/${props.propertyId}/document-wizard/${props.interviewId}/resume`))
        } else {
            ;({ data } = await axios.post(`/properties/${props.propertyId}/document-wizard`, {
                category: props.category,
                template_name: props.templateName,
            }))
        }
        interview.value = data.interview
        showResumeHint.value = data.interview?.status === 'completed'
        applyPage(data.page)
    } catch (err) {
        // The backend Pro gate answers 403 + upgrade_required (e.g. a stale tab
        // or a subscription that lapsed after page load). Hand it to the parent
        // to show the RiskGuard Pro upsell instead of a dead-end error banner.
        if (err.response?.status === 403 && err.response?.data?.upgrade_required) {
            emit('upgrade-required')
            return
        }
        errorMessage.value = err.response?.data?.message || `Could not start the ${documentLabel.value} wizard.`
    } finally {
        loading.value = false
    }
}

const adoptPreview = (newPage) => {
    const next = resolvePreview(previewHtml.value, newPage)
    if (next.changed) {
        previewHtml.value = next.html
    }
}

const applyPage = (newPage) => {
    // Above the cyclical early return below — otherwise the tenant pages, where
    // the landlord is entering the names they most want to watch appear, would
    // be the one place the preview never updates.
    adoptPreview(newPage)

    page.value = newPage

    if (newPage.type === 'cyclical' && newPage.cycle_id) {
        const incoming = Array.isArray(newPage.answers) ? newPage.answers : []
        cycleRows.value = incoming.length > 0 ? incoming.map((r) => ({ ...(r || {}) })) : [blankCycleRow()]
        Object.keys(formAnswers).forEach((k) => delete formAnswers[k])
        return
    }

    cycleRows.value = []
    Object.keys(formAnswers).forEach((k) => delete formAnswers[k])

    // An eviction notice pre-answers nothing (RAC-10343) — see evictionRadioChoice.js
    // for why the reason page in particular must not arrive pre-picked. Decided
    // per question: a radio the landlord has actually clicked comes back, one
    // they have not is blanked however they left the page.
    const chosenFieldIds = new Set(
        Array.isArray(interview.value?.chosen_field_ids) ? interview.value.chosen_field_ids : []
    )

    const answers = newPage.answers || {}
    for (const question of newPage.questions || []) {
        const suppressRadioDefaults = shouldSuppressRadioDefaults({
            isEviction: isEvictionFlow.value,
            question,
            chosenFieldIds,
        })

        for (const field of question.fields || []) {
            if (field.type === 'RADIO' && suppressRadioDefaults) {
                formAnswers[field.id] = false
            } else if (answers && typeof answers === 'object' && field.id in answers) {
                formAnswers[field.id] = answers[field.id]
            } else if (field.type === 'RADIO') {
                formAnswers[field.id] = field.default === true
            } else {
                formAnswers[field.id] = field.default ?? ''
            }
        }
    }
}

const buildAnswersPayload = () => {
    if (isCyclicalPage.value) {
        return { [page.value.cycle_id]: cycleRows.value.map((r) => ({ ...r })) }
    }
    // Only submit answers for currently-visible questions. A question hidden by
    // a showIf rule (e.g. the user switched "Pets allowed?" to No) must not leak
    // its previously-entered answer into the generated lease.
    const visibleIds = visibleStandardFieldIds.value
    const payload = {}
    for (const [key, value] of Object.entries(formAnswers)) {
        if (visibleIds.has(key)) {
            payload[key] = value
        }
    }
    return payload
}

const submit = async (direction, { applyResponse = true, reloadTab = true } = {}) => {
    if (!page.value) {
        return false
    }
    showResumeHint.value = false
    submitting.value = true
    errorMessage.value = null
    try {
        const { data } = await axios.patch(
            `${baseUrl.value}/page/${page.value.pageId}`,
            { answers: buildAnswersPayload(), direction }
        )
        interview.value = data.interview
        if (applyResponse) {
            applyPage(data.page)
        }
        if (reloadTab) {
            // Refresh the surrounding tab so the Resume card's "% complete"
            // reflects the new progress without a full page reload.
            router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
        }
        return true
    } catch (err) {
        errorMessage.value = err.response?.data?.message || 'Could not save your answers. Please try again.'
        return false
    } finally {
        submitting.value = false
    }
}

const persistCurrentAnswers = async () => {
    if (!page.value || page.value.is_terminal) {
        return
    }

    const { data } = await axios.patch(
        `${baseUrl.value}/page/${page.value.pageId}`,
        { answers: buildAnswersPayload(), direction: 'current' }
    )
    interview.value = data.interview
    // Deliberately not applyPage() — that rebuilds formAnswers and would clobber
    // whatever the landlord is typing. Just take the preview: handleComplete()
    // calls this immediately before /completions, so this PATCH carries the
    // final, fully-answered document.
    adoptPreview(data.page)
}

// Refuse to leave a page while a blanked eviction radio is unanswered, and
// surface which question is waiting. Blanking the radio is only half the fix —
// without this, the wizard would sail past an unanswered reason and
// RocketLawyer would fall back to its own default anyway, which is the
// behaviour we are removing.
//
// Shared by BOTH exits, and that is the point (Brad's review of RAC-10343):
// Generate renders as the v-else of Next, so on the final page it IS the exit.
// A last page carrying a radio would otherwise submit every option false and
// hand RocketLawyer its default back on the one page nothing was watching.
const blockedByUnansweredRadio = () => {
    const pending = unansweredRadioQuestion.value

    if (!pending) {
        return false
    }

    errorMessage.value = `Choose an answer to continue: ${pending.title || 'this question'}`

    return true
}

const handleNext = () => {
    if (blockedByUnansweredRadio()) {
        return false
    }

    return submit('next')
}
const handlePrevious = () => submit('previous')
const handleSave = async () => {
    if (await submit('current')) {
        emit('saved')
    }
}

const handleComplete = async () => {
    if (blockedByUnansweredRadio()) {
        return
    }

    finalizing.value = true
    submitting.value = true
    errorMessage.value = null
    try {
        await persistCurrentAnswers()
        const { data } = await axios.post(`${baseUrl.value}/complete`)
        interview.value = data.interview
        emit('completed', data.interview)
        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (err) {
        finalizing.value = false
        errorMessage.value = err.response?.data?.message || `Could not finalize the ${documentLabel.value}.`
        submitting.value = false
    }
}

start()
</script>
